import { useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { AddComplimentaryPeriodForm } from 'src/components/AddComplimentaryPeriodModal/add-complimentary-period-form';
import { AddPreSoldMembershipForm } from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-form';
import { FormDatePicker } from 'src/components/Forms/FormDatePicker/form-date-picker';
import { FormRichTextBubble } from 'src/components/Forms/FormRichTextBubble/form-rich-text-bubble';
import { FormSwitch } from 'src/components/Forms/FormSwitch/form-switch';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import {
  PartnerGroupRules,
  ProjectStages,
  useGroupRules,
  usePartnerGroupRules,
  useProjectStages,
  UserGroupRules,
  useSubmitDay1,
} from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { validateDateInput } from 'src/utils/date';
import { useNotifications } from 'src/utils/hooks';
import {
  getSubmitDay1DefaultValues,
  prepareSubmitDay1Data,
  SubmitDay1FormField,
  SubmitDay1FormValues,
} from './util';

export const SubmitDay1Modal = () => {
  const { closeModal } = useModalMethods(Modals.SubmitDay1);
  const { isOpened, modalProps } = useModalState(Modals.SubmitDay1);
  const { partnerGroupRules } = usePartnerGroupRules(modalProps?.address?.partnerId);
  const { showSuccessAlert } = useNotifications();
  const hasAccessToPSM = partnerGroupRules[PartnerGroupRules.AccessToPresoldMemberships];
  const hasAccessToSubManagementPlus = Boolean(
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagementPlus],
  );
  const { groupRules } = useGroupRules();
  const canUpdateSLA = Boolean(groupRules[UserGroupRules.CanUpdateSLAs]);

  const { projectStages } = useProjectStages({
    filters: [
      {
        attribute: 'projectStageId',
        operator: '=',
        value: ProjectStages.ServiceClient.toString(),
      },
    ],
    limit: 1,
  });

  const { submitDay1 } = useSubmitDay1();

  const hasScheduledPreSoldMembership = Boolean(
    modalProps?.address?.preSoldMemberships?.[0]?.isScheduled,
  );
  const hasPermissionToSeePSM = Boolean(
    hasAccessToPSM && !hasScheduledPreSoldMembership && canUpdateSLA,
  );

  // a hidden section must unregister, or the primary keeps the validity it had while visible
  const formMethods = useForm<SubmitDay1FormValues>({
    defaultValues: getSubmitDay1DefaultValues(modalProps?.project, modalProps?.address),
    mode: 'onChange',
    shouldUnregister: true,
  });
  const { control, formState, handleSubmit } = formMethods;

  const [day1Date, provideComplimentaryPeriod, addPreSoldMembership] = useWatch({
    control,
    name: [
      SubmitDay1FormField.Day1Date,
      SubmitDay1FormField.ProvideComplimentaryPeriod,
      SubmitDay1FormField.AddPreSoldMembership,
    ],
  });
  const showCppForm = canUpdateSLA && Boolean(provideComplimentaryPeriod);
  const showPsmForm = hasPermissionToSeePSM && Boolean(addPreSoldMembership);

  const onSubmit = useCallback(
    async (values: SubmitDay1FormValues) => {
      const project = modalProps?.project;
      const address = modalProps?.address;
      if ((!project?.id && !project?.projectId) || (!address?.id && !address?.addressId)) {
        return;
      }

      const payload = prepareSubmitDay1Data({
        project,
        address,
        values,
        visibleFields: {
          sendEducationalEmails: hasAccessToSubManagementPlus,
          complimentaryPeriod: showCppForm,
          preSoldMembership: showPsmForm,
        },
      });

      closeModal();
      const result = await submitDay1(
        payload,
        {
          ...project,
          projectStage: projectStages?.[0],
        },
        address,
      );
      if (!result.errors) {
        showSuccessAlert('Day1 submitted successfully');
      }
    },
    [
      closeModal,
      hasAccessToSubManagementPlus,
      modalProps,
      projectStages,
      showCppForm,
      showPsmForm,
      showSuccessAlert,
      submitDay1,
    ],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Submit Day 1"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'submit-day1-cancel' },
        {
          label: 'Submit Day 1',
          kind: 'primary',
          onClick: handleSubmit(onSubmit),
          disabled: !formState.isValid,
          testId: 'submit-day1-submit',
        },
      ]}
      onClose={closeModal}
      data-testid="submit-day1-modal"
    >
      <GenericForm<SubmitDay1FormValues> formMethods={formMethods}>
        <FormDatePicker
          type="datepicker"
          name={SubmitDay1FormField.Day1Date}
          label="Day1"
          required="Day 1 date is required"
          rules={{ validate: validateDateInput }}
          data-testid="submit-day1-date"
        />
        {hasAccessToSubManagementPlus ? (
          <FormSwitch
            type="switch"
            name={SubmitDay1FormField.SendEducationalEmails}
            label="Send Educational Emails"
            data-testid="submit-day1-educational-emails-switch"
          />
        ) : null}
        {canUpdateSLA ? (
          <FormSwitch
            type="switch"
            name={SubmitDay1FormField.ProvideComplimentaryPeriod}
            label="Provide Complimentary Membership"
            data-testid="submit-day1-complimentary-switch"
          />
        ) : null}
        {showCppForm ? (
          <AddComplimentaryPeriodForm address={modalProps?.address} baseDate={day1Date} />
        ) : null}
        {hasPermissionToSeePSM ? (
          <FormSwitch
            type="switch"
            name={SubmitDay1FormField.AddPreSoldMembership}
            label="Add Pre-Sold Membership"
            data-testid="submit-day1-pre-sold-switch"
          />
        ) : null}
        {showPsmForm ? <AddPreSoldMembershipForm address={modalProps?.address} /> : null}
        <FormRichTextBubble
          type="rich-text-bubble"
          name={SubmitDay1FormField.MembershipSalesNotes}
          label="Membership Sales Notes"
          placeholder="Enter notes here"
          data-testid="submit-day1-notes"
        />
      </GenericForm>
    </ModalContainer>
  );
};
